import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Drawer, Tooltip } from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import { find } from "lodash";
import { AICC_LANGUAGE_STATUS } from "constants/aiccLanguage";
import { AICC_PROMPT_GROUP_LABEL, aiccPromptGroupFromSlug } from "constants/aiccPrompt";
import { AICC_SANDBOX_BASE_SCOPE } from "constants/aiccPromptSandbox";
import { useLearningAssetsStore } from "../../useLearningAssetsStore";
import { useAiccManagedLanguageNames } from "../../LearningAssets/hooks/useAiccManagedLanguageNames";
import { useAiccPromptsOfGroup } from "../../hooks/useAiccPrompts";
import { useAiccPromptModels } from "../../hooks/useAiccPromptModels";
import LanguageStatusTag from "../LanguageStatusTag";
import Sandbox from ".";
import { SANDBOX_MODE_BY_GROUP } from "./sandboxScope";
import { SANDBOX_DRAWER_WIDTH, SANDBOX_TITLE } from "./configs";
import styles from "./SandboxHost.module.scss";

function SandboxHost() {
  const { group: slug } = useParams<{ group: string }>();
  const group = aiccPromptGroupFromSlug(slug);
  const mode = group ? SANDBOX_MODE_BY_GROUP[group] : undefined;
  const languageId = useLearningAssetsStore((state) => state.languageId);
  const activeStatus = useLearningAssetsStore((state) => state.activeStatus);
  const { versions } = useAiccPromptsOfGroup(mode ? languageId : undefined, group);
  const { languages } = useAiccManagedLanguageNames();
  const { modelsById } = useAiccPromptModels();
  const [isOpen, setIsOpen] = useState(false);

  if (!group || !mode) {
    return null;
  }

  const status = (find(versions, { status: activeStatus }) ?? versions[0])?.status ?? activeStatus;
  const baseScope =
    status === AICC_LANGUAGE_STATUS.public ? AICC_SANDBOX_BASE_SCOPE.live : AICC_SANDBOX_BASE_SCOPE.working;
  const languageName = find(languages, { id: languageId })?.name;
  const title = SANDBOX_TITLE(AICC_PROMPT_GROUP_LABEL[group]);

  return (
    <>
      {!isOpen && (
        <Tooltip title={title} placement="left">
          <Button
            className={styles.floatingButton}
            type="primary"
            shape="circle"
            size="large"
            aria-label={title}
            icon={<ExperimentOutlined />}
            disabled={!languageId || !status}
            onClick={() => setIsOpen(true)}
          />
        </Tooltip>
      )}
      <Drawer
        className={styles.sandboxDrawer}
        visible={isOpen}
        width={SANDBOX_DRAWER_WIDTH}
        title={
          <div className={styles.title}>
            {title}
            <LanguageStatusTag languageName={languageName} status={status} />
          </div>
        }
        onClose={() => setIsOpen(false)}
      >
        <Sandbox
          key={`${languageId}:${mode}:${baseScope}`}
          languageId={languageId}
          languageName={languageName}
          mode={mode}
          baseScope={baseScope}
          modelsById={modelsById}
          isOpen={isOpen}
        />
      </Drawer>
    </>
  );
}

export default SandboxHost;
